import React, { useState, useEffect } from 'react';
import { fetchDistributionMatrix, fetchSavedDistributionPlans, saveDistributionPlan, deleteDistributionPlan } from '../../services/sursysLogisticAPI';
import { Package, Truck, Factory, Activity, Save, AlertTriangle, ArrowRight, Trash2, CheckCircle } from 'lucide-react';

const DistributionMatrixWidget = ({ filters }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savedPlans, setSavedPlans] = useState([]);
  
  // Simulator State
  const [simulatorOpen, setSimulatorOpen] = useState(false);
  const [simItem, setSimItem] = useState(null);
  const [simSource, setSimSource] = useState('');
  const [simTargets, setSimTargets] = useState({});
  const [simSaving, setSimSaving] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [matrix, plans] = await Promise.all([
        fetchDistributionMatrix(filters),
        fetchSavedDistributionPlans()
      ]);
      setData(matrix);
      setSavedPlans(plans);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [filters]);

  const openSimulator = (item) => {
    setSimItem(item);
    setSimSource(item.branches[0].branchId); // Default source
    setSimTargets({});
    setSimulatorOpen(true);
  };

  const handleSimTargetChange = (branchId, value) => {
    setSimTargets(prev => ({
      ...prev,
      [branchId]: parseInt(value) || 0
    }));
  };

  const handleSavePlan = async () => {
    setSimSaving(true);
    const totalOut = Object.values(simTargets).reduce((a, b) => a + b, 0);
    const plan = {
      itemName: simItem.name,
      source: simSource,
      targets: simTargets,
      totalAmount: totalOut
    };
    
    try {
      await saveDistributionPlan(plan);
      const plans = await fetchSavedDistributionPlans();
      setSavedPlans(plans);
      setSimulatorOpen(false);
    } catch (e) {
      console.error(e);
    } finally {
      setSimSaving(false);
    }
  };

  const handleDeletePlan = async (id) => {
    await deleteDistributionPlan(id);
    const plans = await fetchSavedDistributionPlans();
    setSavedPlans(plans);
  };

  const renderSimulator = () => {
    if (!simulatorOpen || !simItem) return null;

    const sourceBranch = simItem.branches.find(b => b.branchId === simSource);
    const totalTransferred = Object.values(simTargets).reduce((a, b) => a + b, 0);
    const remainingStock = sourceBranch.stock - totalTransferred;
    const isSafe = remainingStock >= sourceBranch.minStock;

    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: '#fff', padding: '24px', borderRadius: '12px', width: '90%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '16px', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>Simulasi Distribusi</h3>
            <button onClick={() => setSimulatorOpen(false)} style={{ background: 'transparent', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}>&times;</button>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>Item: {simItem.name}</div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#475569' }}>Ambil Stok Dari Cabang:</label>
            <select 
              value={simSource} 
              onChange={(e) => { setSimSource(e.target.value); setSimTargets({}); }}
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none' }}
            >
              {simItem.branches.map(b => (
                <option key={b.branchId} value={b.branchId}>{b.name} (Stok: {b.stock.toLocaleString()})</option>
              ))}
            </select>
          </div>

          <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#475569' }}>Kirim Ke:</h4>
            {simItem.branches.filter(b => b.branchId !== simSource).map(b => (
              <div key={b.branchId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '14px', color: '#334155' }}>{b.name}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input 
                    type="number" 
                    min="0"
                    placeholder="0"
                    value={simTargets[b.branchId] || ''}
                    onChange={(e) => handleSimTargetChange(b.branchId, e.target.value)}
                    style={{ width: '100px', padding: '6px', borderRadius: '4px', border: '1px solid #cbd5e1', textAlign: 'right' }}
                  />
                  <span style={{ fontSize: '12px', color: '#64748b' }}>pcs</span>
                </div>
              </div>
            ))}
          </div>

          <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '16px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
              <span style={{ color: '#475569' }}>Total Mutasi:</span>
              <span style={{ fontWeight: 'bold' }}>{totalTransferred.toLocaleString()} pcs</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', borderRadius: '8px', background: isSafe ? '#dcfce3' : '#fee2e2' }}>
              <div>
                <div style={{ fontSize: '12px', color: isSafe ? '#166534' : '#991b1b', fontWeight: 'bold', marginBottom: '4px' }}>
                  Sisa Stok {sourceBranch.name}
                </div>
                <div style={{ fontSize: '18px', fontWeight: '900', color: isSafe ? '#15803d' : '#b91c1c' }}>
                  {remainingStock.toLocaleString()}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: isSafe ? '#15803d' : '#b91c1c', fontWeight: 'bold', fontSize: '12px' }}>
                {isSafe ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
                {isSafe ? 'Status Aman' : 'Di Bawah Minimum!'}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button onClick={() => setSimulatorOpen(false)} style={{ padding: '10px 16px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontWeight: '600' }}>Batal</button>
            <button 
              onClick={handleSavePlan} 
              disabled={simSaving || totalTransferred === 0 || !isSafe}
              style={{ padding: '10px 16px', borderRadius: '6px', border: 'none', background: (!isSafe || totalTransferred === 0) ? '#94a3b8' : '#3b82f6', color: '#fff', cursor: (!isSafe || totalTransferred === 0) ? 'not-allowed' : 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              {simSaving ? 'Menyimpan...' : <><Save size={16} /> Simpan Rencana</>}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', background: '#fff', borderRadius: '12px' }}>Loading Matrix...</div>;
  }

  // Generate dynamic branch headers
  const branches = data.length > 0 ? data[0].branches.map(b => ({ id: b.branchId, name: b.name })) : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginBottom: '24px' }}>
      {/* Matrix Table */}
      <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Activity size={20} color="#3b82f6" /> Distribution Sandbox Matrix
            </h2>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>Simulasikan rencana mutasi stok antar cabang</p>
          </div>
        </div>
        
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1200px' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th rowSpan="2" style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '2px solid #cbd5e1', borderRight: '2px solid #e2e8f0', position: 'sticky', left: 0, background: '#f8fafc', zIndex: 10, width: '250px' }}>Item</th>
                {branches.map(b => (
                  <th key={b.id} colSpan="5" style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #e2e8f0', borderRight: '2px solid #e2e8f0', fontSize: '13px', fontWeight: 'bold' }}>{b.name}</th>
                ))}
                <th rowSpan="2" style={{ padding: '12px 16px', textAlign: 'center', borderBottom: '2px solid #cbd5e1', width: '120px' }}>Action</th>
              </tr>
              <tr style={{ background: '#f8fafc' }}>
                {branches.map(b => (
                  <React.Fragment key={b.id + '-sub'}>
                    <th style={{ padding: '6px', textAlign: 'center', fontSize: '11px', borderBottom: '2px solid #cbd5e1', color: '#3b82f6' }}>Stok</th>
                    <th style={{ padding: '6px', textAlign: 'center', fontSize: '11px', borderBottom: '2px solid #cbd5e1', color: '#8b5cf6' }}>Pesanan</th>
                    <th style={{ padding: '6px', textAlign: 'center', fontSize: '11px', borderBottom: '2px solid #cbd5e1', color: '#0ea5e9' }}>Serapan YoY</th>
                    <th style={{ padding: '6px', textAlign: 'center', fontSize: '11px', borderBottom: '2px solid #cbd5e1', color: '#f59e0b' }}>WIP</th>
                    <th style={{ padding: '6px', textAlign: 'center', fontSize: '11px', borderBottom: '2px solid #cbd5e1', borderRight: '2px solid #e2e8f0', color: '#10b981' }}>Transit</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((item, idx) => (
                <tr key={item.id} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '12px 16px', fontWeight: '600', fontSize: '13px', borderRight: '2px solid #e2e8f0', position: 'sticky', left: 0, background: idx % 2 === 0 ? '#fff' : '#f8fafc', zIndex: 10 }}>
                    {item.name}
                    {item.noJob && (
                      <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: '#7c3aed', fontFamily: 'monospace', background: '#ede9fe', padding: '1px 6px', borderRadius: 4 }}>
                          <Factory size={9} style={{ verticalAlign: 'middle', marginRight: 3 }} />#{item.noJob}
                        </span>
                        {item.wipLabel && (
                          <span style={{ fontSize: 10, fontWeight: 700, color: '#b45309', background: '#fef3c7', padding: '1px 6px', borderRadius: 4 }}>
                            WIP: {item.wipLabel}{item.nationalWip > 0 ? ` · ${item.nationalWip.toLocaleString()} pcs` : ''}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  {item.branches.map(b => {
                    const isCritical = b.stock < b.demand;
                    return (
                      <React.Fragment key={b.branchId}>
                        <td style={{ padding: '12px 6px', textAlign: 'center', fontSize: '12px', fontWeight: 'bold', color: isCritical ? '#ef4444' : '#1e293b' }}>
                          {b.stock.toLocaleString()}
                        </td>
                        <td style={{ padding: '12px 6px', textAlign: 'center', fontSize: '12px', color: '#8b5cf6', fontWeight: 'bold' }}>
                          {b.demand ? b.demand.toLocaleString() : '-'}
                        </td>
                        <td style={{ padding: '12px 6px', textAlign: 'center', fontSize: '12px', color: '#0ea5e9' }}>
                          {b.lastYearSales ? b.lastYearSales.toLocaleString() : '-'}
                        </td>
                        <td style={{ padding: '12px 6px', textAlign: 'center', fontSize: '12px', color: '#64748b' }}>
                          {b.wip > 0 ? b.wip.toLocaleString() : '-'}
                        </td>
                        <td style={{ padding: '12px 6px', textAlign: 'center', fontSize: '12px', color: '#64748b', borderRight: '2px solid #e2e8f0' }}>
                          {b.transit > 0 ? b.transit.toLocaleString() : '-'}
                        </td>
                      </React.Fragment>
                    );
                  })}
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <button onClick={() => openSimulator(item)} style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}>
                      Simulasi
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Saved Plans */}
      {savedPlans.length > 0 && (
        <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', padding: '24px' }}>
          <h2 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 'bold', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Save size={18} color="#10b981" /> Rencana Mutasi (Drafts)
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
            {savedPlans.map(plan => (
              <div key={plan.id} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px', background: '#f8fafc' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div>
                    <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 'bold', marginBottom: '2px' }}>{plan.id}</div>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#0f172a' }}>{plan.itemName}</div>
                  </div>
                  <button onClick={() => handleDeletePlan(plan.id)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ef4444' }}>
                    <Trash2 size={16} />
                  </button>
                </div>
                
                <div style={{ fontSize: '12px', color: '#475569', marginBottom: '8px' }}>
                  Dari: <strong>{plan.source}</strong> (Total: {plan.totalAmount.toLocaleString()} pcs)
                </div>
                
                <div style={{ background: '#fff', padding: '8px', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                  {Object.entries(plan.targets).map(([target, qty]) => qty > 0 ? (
                    <div key={target} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#64748b' }}><ArrowRight size={12}/> Ke {target}</span>
                      <span style={{ fontWeight: 'bold' }}>{qty.toLocaleString()} pcs</span>
                    </div>
                  ) : null)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {renderSimulator()}
    </div>
  );
};

export default DistributionMatrixWidget;
